import { useState, useEffect } from "react";
import DashboardLayout from "../Layouts/DashboardLayout";
import { Plus, Pencil, Trash2 } from "lucide-react";

const presetColors = [
  "#F59E0B",
  "#10B981",
  "#3B82F6",
  "#8B5CF6",
  "#EF4444",
  "#EC4899",
  "#22D3EE",
];

export default function VendorTypes() {
  const [types, setTypes] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);

  const emptyForm = {
    name: "",
    description: "",
    color: presetColors[0],
    status: "Active",
  };

  const [form, setForm] = useState(emptyForm);

  // 🔥 FETCH DATA
  const fetchTypes = async () => {
    try {
      const res = await fetch("/api/vendor-types", {
        credentials: "include",
      });
      
      const data = await res.json();

      if (data.status === "success") {
        setTypes(data.data);
      }
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    fetchTypes();
  }, []);

  const openAdd = () => {
    setForm(emptyForm);
    setEditing(null);
    setShowModal(true);
  };

  const openEdit = (type) => {
    setForm(type);
    setEditing(type.id);
    setShowModal(true);
  };

  // 🔥 SAVE (CREATE / UPDATE)
  const saveType = async () => {
    if (!form.name.trim()) return;

    try {
      const url = editing
        ? `/api/vendor-types/${editing}`
        : "/api/vendor-types";

      const method = editing ? "PUT" : "POST";

      await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify(form),
      });

      await fetchTypes();
      setShowModal(false);
    } catch (err) {
      console.error(err);
      alert("Error saving vendor type");
    }
  };

  // 🔥 DELETE
  const removeType = async (id) => {
    try {
      await fetch(`/api/vendor-types/${id}`, {
        method: "DELETE",
        credentials: "include",
      });

      setTypes((prev) => prev.filter((t) => t.id !== id));
    } catch (err) {
      console.error(err);
      alert("Delete failed");
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-8">

        {/* HEADER */}
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-serif">Vendor Types</h1>
          <button
            onClick={openAdd}
            className="flex items-center gap-2 px-5 py-2 rounded-xl bg-amber-400 text-black font-medium hover:bg-amber-300 transition"
          >
            <Plus size={16} />
            Add Vendor Type
          </button>
        </div>

        {/* EMPTY STATE */}
        {types.length === 0 && (
          <div className="rounded-2xl bg-white/5 border border-white/10 p-12 text-center">
            <p className="text-lg font-medium">No vendor types added yet</p>
            <p className="text-sm text-gray-400 mt-1">
              Create vendor categories like Dairy, Dry Fruits, Packaging, etc.
            </p>

            <button
              onClick={openAdd}
              className="mt-6 px-6 py-2 rounded-xl bg-amber-400 text-black font-medium"
            >
              + Add First Vendor Type
            </button>
          </div>
        )}

        {/* TABLE */}
        {types.length > 0 && (
          <div className="rounded-2xl bg-white/5 border border-white/10 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-black/40 text-gray-400">
                <tr>
                  <th className="px-6 py-4 text-left">Type</th>
                  <th className="px-6 py-4 text-left">Description</th>
                  <th className="px-6 py-4 text-center">Status</th>
                  <th className="px-6 py-4 text-right">Actions</th>
                </tr>
              </thead>

              <tbody>
                {types.map((t) => (
                  <tr key={t.id} className="border-t border-white/10 hover:bg-white/5">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <span
                          className="w-3 h-3 rounded-full"
                          style={{ backgroundColor: t.color }}
                        />
                        <span className="font-medium">{t.name}</span>
                      </div>
                    </td>

                    <td className="px-6 py-4 text-gray-400">
                      {t.description || "-"}
                    </td>

                    <td className="px-6 py-4 text-center">
                      <span
                        className={`px-3 py-1 rounded-full text-xs ${
                          t.status === "Active"
                            ? "bg-emerald-500/20 text-emerald-400"
                            : "bg-red-500/20 text-red-400"
                        }`}
                      >
                        {t.status}
                      </span>
                    </td>

                    <td className="px-6 py-4 text-right">
                      <div className="flex justify-end gap-4">
                        <button
                          onClick={() => openEdit(t)}
                          className="text-amber-400 hover:underline flex items-center gap-1"
                        >
                          <Pencil size={14} />
                          Edit
                        </button>
                        <button
                          onClick={() => removeType(t.id)}
                          className="text-red-400 hover:underline flex items-center gap-1"
                        >
                          <Trash2 size={14} />
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* MODAL */}
        {showModal && (
          <Modal onClose={() => setShowModal(false)}>
            <h2 className="text-xl font-serif mb-6">
              {editing ? "Edit Vendor Type" : "Add Vendor Type"}
            </h2>

            <div className="space-y-4">
              <input
                placeholder="Vendor Type Name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="w-full px-4 py-2 rounded-lg bg-black/40 border border-white/10"
              />

              <textarea
                placeholder="Description"
                value={form.description}
                onChange={(e) =>
                  setForm({ ...form, description: e.target.value })
                }
                className="w-full px-4 py-2 rounded-lg bg-black/40 border border-white/10"
              />

              {/* COLOR PICKER */}
              <div>
                <p className="text-xs text-gray-400 mb-2">Color Tag</p>
                <div className="flex gap-2 flex-wrap">
                  {presetColors.map((c) => (
                    <button
                      key={c}
                      onClick={() => setForm({ ...form, color: c })}
                      className={`w-7 h-7 rounded-full border ${
                        form.color === c ? "ring-2 ring-white" : ""
                      }`}
                      style={{ backgroundColor: c }}
                    />
                  ))}
                </div>
              </div>

              <select
                value={form.status}
                onChange={(e) => setForm({ ...form, status: e.target.value })}
                className="w-full px-4 py-2 rounded-lg bg-black/40 border border-white/10"
              >
                <option>Active</option>
                <option>Disabled</option>
              </select>
            </div>

            <div className="flex justify-end gap-4 mt-8">
              <button
                onClick={() => setShowModal(false)}
                className="px-6 py-2 rounded-xl border border-white/20"
              >
                Cancel
              </button>
              <button
                onClick={saveType}
                className="px-6 py-2 rounded-xl bg-amber-400 text-black font-medium"
              >
                Save
              </button>
            </div>
          </Modal>
        )}
      </div>
    </DashboardLayout>
  );
}

function Modal({ children, onClose }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center">
      <div className="w-full max-w-xl rounded-2xl bg-[#0b0b0f] border border-white/10 p-8 relative">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-white"
        >
          ✕
        </button>
        {children}
      </div>
    </div>
  );
}